import { useMemo, useState, type ReactNode } from "react";
import {
  Controller,
  useFieldArray,
  useWatch,
  type ArrayPath,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import { twMerge } from "tailwind-merge";

import Icons from "assets";
import { Box } from "ui/box";
import { Button } from "ui/button";
import { Collapse } from "ui/collapse";
import { Switch } from "ui/switch";
import { Typography } from "ui/typography";

import {
  deriveFieldDefaults,
  getConditionalDependentFieldNames,
  getDynamicSectionConfig,
  getDynamicSectionEditHeaderOnlyFieldNames,
  getFieldTitle,
  getRowGroups,
  isFieldHiddenInEdit,
  resolveDynamicSectionItemLabel,
  sortJsonFormFields,
  type JsonFormObjectSchema,
  type JsonFormSchema,
} from "containers/admin/clients/client/components/form-builder/form-schema";
import { renderGroupedPropertyEntries } from "containers/admin/clients/client/components/form-builder/form-builder-grouped-fields";

type RenderDynamicSectionField = (
  name: string,
  field: JsonFormSchema,
  parentSchema: JsonFormObjectSchema,
  prefix: string,
) => ReactNode;

type FormBuilderDynamicSectionProps<T extends FieldValues> = {
  fieldName: ArrayPath<T>;
  field: JsonFormSchema;
  control: Control<T>;
  renderField: RenderDynamicSectionField;
};

const getItemSchema = (field: JsonFormSchema): JsonFormObjectSchema | undefined => {
  if (field.items?.type !== "object" || !field.items.properties) {
    return undefined;
  }

  return field.items as JsonFormObjectSchema;
};

export const FormBuilderDynamicSection = <T extends FieldValues>({
  fieldName,
  field,
  control,
  renderField,
}: FormBuilderDynamicSectionProps<T>) => {
  const itemSchema = getItemSchema(field);
  const config = getDynamicSectionConfig(field);
  const sectionTitle = getFieldTitle(String(fieldName), field);
  const headerFieldNames = useMemo(() => getDynamicSectionEditHeaderOnlyFieldNames(field), [field]);
  const isCollapsibleItem = config.collapsible !== false;
  const [collapsedItems, setCollapsedItems] = useState<Record<number, boolean>>({});

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldName,
    keyName: "_dynamicSectionId",
  });
  const watchedItems = useWatch({
    control,
    name: fieldName as Path<T>,
  }) as Record<string, unknown>[] | undefined;

  const canDeleteItem = fields.length > Math.max(config.minItems, 1);

  const emptyItem = useMemo(
    () => (itemSchema ? deriveFieldDefaults(itemSchema.properties) : {}),
    [itemSchema],
  );

  const itemSchemaWithLayout = useMemo((): JsonFormObjectSchema | undefined => {
    if (!itemSchema) {
      return undefined;
    }

    const rowGroups = getRowGroups(itemSchema);

    if (rowGroups.length > 0 || !config.rowGroups?.length) {
      return itemSchema;
    }

    return {
      ...itemSchema,
      "x-row-groups": config.rowGroups,
    };
  }, [config.rowGroups, itemSchema]);

  const resolveItemRow = (index: number): Record<string, unknown> => watchedItems?.[index] ?? {};

  const resolveItemLabel = (index: number) =>
    resolveDynamicSectionItemLabel(resolveItemRow(index), index, field);

  const resolveItemSubtitle = (index: number): string | null => {
    if (!config.itemSubtitleField) {
      return null;
    }

    const value = resolveItemRow(index)[config.itemSubtitleField];

    if (value === undefined || value === null || String(value).trim() === "") {
      return null;
    }

    return `${config.itemSubtitlePrefix ?? ""}${String(value)}`;
  };

  const renderAddButton = () =>
    !!config.addButtonLabel && (
      <Button
        type="button"
        variant="contained"
        size="medium"
        onClick={() => {
          const nextIndex = fields.length;
          const newItem = config.generateIdField
            ? { ...emptyItem, [config.generateIdField]: crypto.randomUUID() }
            : { ...emptyItem };

          append(newItem as never, { shouldFocus: false });
          setCollapsedItems((previous) => ({
            ...previous,
            [nextIndex]: false,
          }));
        }}
      >
        {config.addButtonLabel}
      </Button>
    );

  if (!itemSchema) {
    return null;
  }

  // Non-user-managed sections (no `addButtonLabel` in the schema at all, no empty-state copy)
  // render nothing when their derived rows are empty, instead of an empty heading/chrome with
  // nowhere to add from. Reads the raw schema value rather than `config.addButtonLabel`, which
  // defaults to "Add item" — every dynamic-section that wants a real add button still gets one.
  if (fields.length === 0 && !field["x-dynamic-section"]?.addButtonLabel && !config.emptyMessage) {
    return null;
  }

  if (fields.length === 0 && config.emptyMessage) {
    return (
      <div className="flex flex-col gap-1.5">
        {sectionTitle.trim() && (
          <Typography component="h5" variant="subtitle1" sx={(theme) => ({ color: theme.palette.secondary.main })}>
            {sectionTitle}
          </Typography>
        )}
        <p className="text-base font-semibold leading-6 text-gray-600">{config.emptyMessage}</p>
        {renderAddButton()}
      </div>
    );
  }

  const toggleCollapsed = (index: number) => {
    setCollapsedItems((previous) => {
      const currentlyCollapsed = previous[index] ?? config.defaultCollapsed;

      return {
        ...previous,
        [index]: !currentlyCollapsed,
      };
    });
  };

  const renderItemFields = (index: number) => {
    const prefix = `${String(fieldName)}.${index}`;
    const layoutSchema = itemSchemaWithLayout ?? itemSchema;
    const dependentFieldNames = getConditionalDependentFieldNames(layoutSchema);
    const entries = sortJsonFormFields(Object.entries(layoutSchema.properties)).filter(
      ([name, itemField]) =>
        !dependentFieldNames.has(name) &&
        !headerFieldNames.has(name) &&
        !isFieldHiddenInEdit(itemField),
    );

    return renderGroupedPropertyEntries(
      layoutSchema,
      prefix,
      entries,
      ([name, itemField]) => renderField(name, itemField, layoutSchema, prefix),
      (name) => resolveItemRow(index)[name],
    );
  };

  const renderHeaderToggle = (index: number) => {
    if (!config.headerToggleField) {
      return null;
    }

    const toggleField = itemSchema.properties[config.headerToggleField];

    if (!toggleField) {
      return null;
    }

    const toggleFieldName = `${String(fieldName)}.${index}.${config.headerToggleField}` as Path<T>;

    return (
      <Controller
        name={toggleFieldName}
        control={control}
        render={({ field: { onChange, value } }) => (
          <Switch
            toggleName={getFieldTitle(config.headerToggleField as string, toggleField)}
            checked={Boolean(value)}
            onChange={onChange}
            labelPlacement="start"
            sx={{ flexShrink: 0 }}
          />
        )}
      />
    );
  };

  if (config.chromeless) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {sectionTitle.trim() && (
          <Typography component="h5" variant="subtitle1" sx={(theme) => ({ color: theme.palette.grey[900] })}>
            {sectionTitle}
          </Typography>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }} data-testid="chromeless-dynamic-section-items">
          {fields.map((item, index) => (
            <Box
              key={item._dynamicSectionId}
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
              data-testid={`dynamic-section-item-${index}`}
            >
              {renderItemFields(index)}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      {sectionTitle.trim() && (
        <Typography component="h5" variant="subtitle1" sx={(theme) => ({ color: theme.palette.grey[900] })}>
          {sectionTitle}
        </Typography>
      )}
      <div className="flex flex-col gap-4">
        {fields.map((item, index) => {
          const isCollapsed = isCollapsibleItem
            ? (collapsedItems[index] ?? config.defaultCollapsed)
            : false;
          const itemLabel = resolveItemLabel(index);
          const subtitle = resolveItemSubtitle(index);
          const headerToggle = renderHeaderToggle(index);
          const showItemLabel = !(config.hideSingleItemLabel && fields.length === 1);
          const showHeaderRow = showItemLabel || Boolean(headerToggle);

          return (
            <div
              key={item._dynamicSectionId}
              className={twMerge(
                "flex flex-col gap-2",
                isCollapsibleItem && "rounded-md border border-navy-200 px-3 py-2.5",
              )}
            >
              {showHeaderRow && (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    {showItemLabel &&
                      (isCollapsibleItem ? (
                        <button
                          type="button"
                          className="flex w-fit min-w-0 items-center gap-1"
                          aria-expanded={!isCollapsed}
                          onClick={() => toggleCollapsed(index)}
                        >
                          <span className="text-md font-bold leading-5 text-gray-900">
                            {itemLabel}
                          </span>
                          <Icons.ChevronDown
                            aria-hidden
                            className={twMerge(
                              "size-5 text-gray-900 transition-transform",
                              isCollapsed && "-rotate-90 text-aqua-600",
                            )}
                          />
                        </button>
                      ) : (
                        <span className="text-sm font-bold leading-5 text-navy-800">
                          {itemLabel}
                        </span>
                      ))}
                    {subtitle && (
                      <p className="text-sm font-semibold leading-5 tracking-[0.25px] text-gray-500">
                        {subtitle}
                      </p>
                    )}
                  </div>
                  {headerToggle}
                </div>
              )}
              {isCollapsibleItem ? (
                /* `unmountOnExit` keeps the collapsed item out of the DOM, as the plain conditional
                   render did: it is the card's own gap that would otherwise show under the header,
                   and react-hook-form that would otherwise keep rendering hidden inputs. */
                <Collapse in={!isCollapsed} unmountOnExit>
                  <div className="flex flex-col gap-3">
                    {renderItemFields(index)}
                    {canDeleteItem && !!config.deleteButtonLabel && (
                      <Button
                        type="button"
                        variant="text"
                        aria-label={`${config.deleteButtonLabel} ${itemLabel}`}
                        startIcon={<Icons.Trash />}
                        onClick={() => remove(index)}
                      >
                        {config.deleteButtonLabel}
                      </Button>
                    )}
                  </div>
                </Collapse>
              ) : (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-3 rounded border border-gray-200 p-2.5">
                    {renderItemFields(index)}
                  </div>
                  {canDeleteItem && !!config.deleteButtonLabel && (
                    <Button
                      type="button"
                      variant="text"
                      aria-label={`${config.deleteButtonLabel} ${itemLabel}`}
                      startIcon={<Icons.Trash />}
                      onClick={() => remove(index)}
                    >
                      {config.deleteButtonLabel}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {renderAddButton()}
      </div>
    </div>
  );
};
